import { CALL_OUTCOMES, CALL_OUTCOME_LABELS, type CallOutcome, type LoggableActivityType } from '@takumi/shared';
import { CalendarClock, Mail, MessageCircle, NotebookPen, Phone, Users } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Select, Textarea } from '@/components/ui/form';
import { Dialog, DialogContent } from '@/components/ui/primitives';
import { useLogActivity } from '@/lib/queries';
import type { Contact, Task } from '@/lib/types';
import { cn } from '@/lib/utils';
import { defaultSchedule, FollowupScheduler, type ScheduleValue } from './FollowupScheduler';

const TYPES: { key: LoggableActivityType; label: string; icon: ReactNode }[] = [
  { key: 'call', label: 'שיחה', icon: <Phone /> },
  { key: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle /> },
  { key: 'email', label: 'אימייל', icon: <Mail /> },
  { key: 'meeting', label: 'פגישה', icon: <Users /> },
  { key: 'note', label: 'הערה', icon: <NotebookPen /> },
];

/**
 * The core CRM loop in one dialog: what happened → (close the current follow-up) → when is the next one.
 */
export function LogActivityDialog({
  open,
  onOpenChange,
  orgId,
  orgName,
  contacts,
  openFollowup,
  defaultType = 'call',
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  orgId: string;
  orgName: string;
  contacts: Pick<Contact, 'id' | 'fullName' | 'isPrimary'>[];
  openFollowup?: Pick<Task, 'id' | 'title' | 'dueAt'> | null;
  defaultType?: LoggableActivityType;
}) {
  const [type, setType] = useState<LoggableActivityType>(defaultType);
  const [contactId, setContactId] = useState('');
  const [outcome, setOutcome] = useState<CallOutcome>('answered');
  const [body, setBody] = useState('');
  const [completeCurrent, setCompleteCurrent] = useState(true);
  const [schedule, setSchedule] = useState<ScheduleValue>(defaultSchedule);
  const mutation = useLogActivity(orgId);

  useEffect(() => {
    if (open) {
      setType(defaultType);
      setContactId(contacts.find((c) => c.isPrimary)?.id ?? contacts[0]?.id ?? '');
      setOutcome('answered');
      setBody('');
      setCompleteCurrent(defaultType !== 'note');
      // A pure note usually doesn't change the follow-up plan.
      setSchedule(defaultType === 'note' && openFollowup ? { preset: 'none', dueAt: null } : defaultSchedule());
    }
  }, [open, defaultType, contacts, openFollowup]);

  // If there is still an open follow-up that stays open, "no new follow-up" is fine and not a warning.
  const keepsExisting = !!openFollowup && !completeCurrent;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (type === 'note' && !body.trim()) {
      toast.error('יש לכתוב את תוכן ההערה');
      return;
    }
    const res = await mutation.mutateAsync({
      type,
      contactId: contactId || null,
      body: body.trim() || null,
      callOutcome: type === 'call' ? outcome : null,
      completeTaskId: openFollowup && completeCurrent ? openFollowup.id : null,
      nextFollowupAt: schedule.dueAt ? schedule.dueAt.toISOString() : null,
    } as never);
    toast.success(res.movedStage ? 'נרשם — הליד עבר לשלב "יצירת קשר"' : 'הפעילות נרשמה');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="רישום פעילות" description={orgName} wide>
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="סוג הפעילות">
            {TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                role="radio"
                aria-checked={type === t.key}
                onClick={() => {
                  setType(t.key);
                  // Contacting the customer usually closes the pending follow-up; writing a note does not.
                  setCompleteCurrent(t.key !== 'note');
                }}
                className={cn(
                  'flex items-center gap-1.5 rounded-control border px-3 py-2 text-sm font-semibold [&_svg]:size-4',
                  type === t.key ? 'border-matcha-600 bg-matcha-50 text-matcha-800' : 'border-line-strong text-ink-soft hover:bg-sunken',
                )}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {contacts.length > 0 && (
              <Field label="איש קשר">
                {(id) => (
                  <Select id={id} value={contactId} onChange={(e) => setContactId(e.target.value)}>
                    <option value="">—</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.fullName}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            {type === 'call' && (
              <Field label="תוצאת השיחה">
                {(id) => (
                  <Select id={id} value={outcome} onChange={(e) => setOutcome(e.target.value as CallOutcome)}>
                    {CALL_OUTCOMES.map((o) => (
                      <option key={o} value={o}>
                        {CALL_OUTCOME_LABELS[o]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
          </div>

          <Field label={type === 'note' ? 'הערה' : 'סיכום'} required={type === 'note'}>
            {(id) => (
              <Textarea
                id={id}
                rows={4}
                autoFocus
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={type === 'call' ? 'על מה דיברנו? מה הצעד הבא?' : type === 'note' ? 'מה חשוב לזכור על הלקוח?' : 'פרטים…'}
              />
            )}
          </Field>

          {openFollowup && (
            <label className="flex cursor-pointer items-start gap-2.5 rounded-control border border-line bg-sunken/60 px-3 py-2.5 text-sm">
              <input type="checkbox" className="mt-0.5 size-4 accent-matcha-700" checked={completeCurrent} onChange={(e) => setCompleteCurrent(e.target.checked)} />
              <span>
                סימון הפולואפ הנוכחי כבוצע: <strong>{openFollowup.title}</strong>
              </span>
            </label>
          )}

          <div className="rounded-card border border-matcha-200 bg-matcha-50/60 p-4">
            <div className="mb-3 flex items-center gap-2 font-semibold text-matcha-900">
              <CalendarClock className="size-4" />
              מתי הפולואפ הבא?
            </div>
            <FollowupScheduler value={schedule} onChange={setSchedule} allowNone />
            {keepsExisting && schedule.preset === 'none' && <p className="mt-2 text-xs text-muted">הפולואפ הקיים יישאר פתוח.</p>}
          </div>

          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              ביטול
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              שמירה
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
